import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ChefHat, AlarmClock, Sparkles } from "lucide-react";
import api from "../lib/api";

const QUICK = [15, 30, 45, 60];

export const fmtReadyAt = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "");

// Mini-popup "In preparazione": scelta rapida minuti o valore personalizzato; opzionale (si può saltare)
// mode="reschedule": nuovo orario su ordine già in preparazione → il cliente riceve le scuse con il nuovo orario
export default function OrderEtaDialog({ order, onConfirm, onClose, busy = false, mode = "start" }) {
  const [custom, setCustom] = useState("");
  const [sugg, setSugg] = useState(null);
  const open = !!order;
  const resched = mode === "reschedule";
  const orderId = order?.order_id || order?.id;
  useEffect(() => {
    setSugg(null);
    if (!orderId) return;
    let alive = true;
    api.get(`/customer-orders/${orderId}/eta-suggestion`).then((r) => alive && setSugg(r.data)).catch(() => {});
    return () => { alive = false; };
  }, [orderId]);
  const customVal = parseInt(custom, 10);
  const customOk = Number.isFinite(customVal) && customVal >= 1 && customVal <= 600;
  const preview = (m) => new Date(Date.now() + m * 60000).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-sm" data-testid="order-eta-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            {resched ? <AlarmClock className="h-4 w-4 text-red-300" /> : <ChefHat className="h-4 w-4 text-amber-300" />}
            {resched ? "Nuovo orario di ritiro" : "Tempo di attesa"}
          </DialogTitle>
          <DialogDescription className="sr-only">Scegli i minuti di attesa per l'ordine</DialogDescription>
        </DialogHeader>
        {order && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-400" data-testid="order-eta-intro">
              {resched ? (
                <>Ordine di <b className="text-zinc-100">{order.customer_name || order.title?.replace(/^.*· /, "") || "cliente"}</b>
                  {order.ready_at ? <> · era previsto per le <b className="text-red-300">{fmtReadyAt(order.ready_at)}</b></> : null}. Quanto manca ancora?
                  Il cliente riceve un messaggio di <b className="text-zinc-200">scuse con il nuovo orario</b>.</>
              ) : (
                <>Ordine di <b className="text-zinc-100">{order.customer_name || order.title?.replace(/^.*da /, "") || "cliente"}</b>: quanto ci vuole?
                  Il cliente riceve l'orario di ritiro stimato.</>
              )}
            </p>
            {sugg?.suggested_minutes && !resched && (
              <button type="button" disabled={busy} onClick={() => onConfirm(sugg.suggested_minutes)} data-testid="order-eta-suggested"
                className="w-full rounded-lg border border-amber-500/60 bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2.5 flex items-center justify-between gap-3 text-left transition-colors disabled:opacity-50">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-300" />
                  <span>
                    <span className="font-heading font-bold text-lg text-amber-200">Consigliato: {sugg.suggested_minutes}′</span>
                    <span className="block text-[11px] text-zinc-400" data-testid="order-eta-suggested-basis">
                      {sugg.basis === "branch" ? `media reale sede ${sugg.branch_name || ""}` : "media reale di tutte le sedi"} · {sugg.sample} ordini · 30 gg
                    </span>
                  </span>
                </span>
                <span className="text-[11px] text-zinc-500">≈ {preview(sugg.suggested_minutes)}</span>
              </button>
            )}
            <div className="grid grid-cols-4 gap-2">
              {QUICK.map((m) => (
                <button key={m} type="button" disabled={busy} onClick={() => onConfirm(m)} data-testid={`order-eta-quick-${m}`}
                  className="rounded-lg border border-zinc-800 bg-zinc-950 hover:border-amber-500 hover:bg-amber-500/10 py-2.5 text-center transition-colors disabled:opacity-50">
                  <div className="font-heading font-bold text-lg text-zinc-100">{m}′</div>
                  <div className="text-[10px] text-zinc-500">≈ {preview(m)}</div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" min={1} max={600} inputMode="numeric" placeholder="Minuti personalizzati" value={custom}
                onChange={(e) => setCustom(e.target.value)} data-testid="order-eta-custom-input"
                className="bg-zinc-950 border-zinc-800" />
              <Button type="button" disabled={!customOk || busy} onClick={() => onConfirm(customVal)} data-testid="order-eta-custom-confirm"
                className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] shrink-0">
                OK{customOk ? ` · ${preview(customVal)}` : ""}
              </Button>
            </div>
            <div className="flex justify-between items-center pt-1">
              {resched
                ? <span className="text-[11px] text-zinc-600">Invio push + WhatsApp al cliente</span>
                : <button type="button" disabled={busy} onClick={() => onConfirm(null)} data-testid="order-eta-skip"
                    className="text-xs text-zinc-500 hover:text-zinc-300 underline-offset-2 hover:underline">Salta, senza stima</button>}
              <button type="button" disabled={busy} onClick={onClose} data-testid="order-eta-cancel" className="text-xs text-zinc-400 hover:text-zinc-200">Annulla</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
